import React, { useEffect, useMemo, useState } from "react";
import { Table, Tag, Button, Modal, Spin, Descriptions, Alert, Row, Col, Card, Statistic, Input, Select, Space, message } from "antd";
import {
  BAND_COLORS, EVALUATION_LABELS, PENALTY_REASONS, fmtDec, fmtDay, teacherName, reviewerLabel, downloadCSV, apiError, ACTIVE_OPTIONS, matchesActive,
} from "./utils";

const axios = require("axios").default;

const CANCEL_LABELS = { 1: "Yönetici iptali", 4: "Yönetici iptali", 6: "Öğrenci iptali" };

/** Tek eğitmenin skor dökümü, iptal/ihlal olayları ve öğrenci yorumları */
const PerformanceDetail = ({ teacherId, days, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [t, setT] = useState(null);

  useEffect(() => {
    if (!teacherId) return;
    setT(null);
    setLoading(true);
    axios
      .post(`payroll/teacher/${teacherId}`, { dates: days })
      .then(({ data }) => setT(data.teacher))
      .catch((err) => message.error(apiError(err, "Performans detayı alınamadı.")))
      .finally(() => setLoading(false));
  }, [teacherId]); // eslint-disable-line react-hooks/exhaustive-deps

  const p = t?.perf;
  const manual = (t?.detail.penalties || []).filter((x) => ["miss", "late", "quality"].includes(x.reason));

  return (
    <Modal visible={!!teacherId} onCancel={onClose} footer={null} width={900} title={t ? teacherName(t) : "Performans"}>
      <Spin spinning={loading}>
        {t && (
          <>
            <Descriptions bordered size="small" column={{ xs: 1, sm: 2, md: 4 }} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Toplam skor">
                <b>{fmtDec(p.score)}</b> / 100 <Tag color={BAND_COLORS[p.band]}>{p.band}</Tag>
                {p.provisional && <Tag>Geçici</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="İptal puanı">{fmtDec(p.cancellation)} / 40</Descriptions.Item>
              <Descriptions.Item label="Disiplin">{fmtDec(p.discipline)} / 25</Descriptions.Item>
              <Descriptions.Item label="Yorum">{fmtDec(p.reviews)} / 35</Descriptions.Item>
              <Descriptions.Item label="Rezervasyon (adet)">{p.bookings}</Descriptions.Item>
              <Descriptions.Item label="Ortalama yıldız">{p.reviewCount ? `${fmtDec(p.avgStars)} (${p.reviewCount} yorum)` : "-"}</Descriptions.Item>
              <Descriptions.Item label="Düzeltilmiş yıldız">{fmtDec(p.adjustedStars)}</Descriptions.Item>
              <Descriptions.Item label="Öğrenci iptali">{t.studentCancels} <span style={{ color: "#888" }}>(puanı düşürmez)</span></Descriptions.Item>
            </Descriptions>
            {p.invalid && (
              <Alert type="warning" showIcon style={{ marginBottom: 12 }}
                message="İptal + kaçırılan ders adedi rezervasyon adedinden fazla; veriler kontrol edilmeli." />
            )}

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <h4>İptaller ve manuel devamsızlıklar</h4>
                <div style={{ fontSize: 12, color: "#888", marginBottom: 8 }}>
                  İptaller sistemden gelir. Kaçırılan ders / geç kalma / kalite ihlali yalnızca Maaş Yönetimi → Eğitmen Detayı → Ceza Ekle ile girilir.
                </div>
                {[...t.detail.cancellations.map((c) => ({
                  key: "c" + c.id, day: c.day,
                  title: CANCEL_LABELS[Number(c.status)],
                  sub: `${c.start_time} - ${c.end_time}${c.student_first_name ? ` · ${c.student_first_name} ${c.student_last_name}` : ""} · Sistem`,
                  counts: Number(c.status) !== 6,
                })),
                ...manual.map((x) => ({
                  key: "p" + x.id, day: x.day,
                  title: `${PENALTY_REASONS[x.reason]}${x.event_count > 1 ? ` × ${x.event_count}` : ""}`,
                  sub: `${x.status === "approved" ? "Onaylı" : "Onay bekliyor"} · Manuel${x.note ? ` · ${x.note}` : ""}`,
                  counts: x.status === "approved" && x.source === "teacher",
                }))]
                  .sort((a, b) => (a.day < b.day ? 1 : -1))
                  .map((e) => (
                    <div key={e.key} style={{ borderBottom: "1px solid #f5f5f5", padding: "6px 0", opacity: e.counts ? 1 : 0.6 }}>
                      <b>{e.title}</b> <span style={{ color: "#888", fontSize: 12 }}>· {fmtDay(e.day)}</span>
                      <div style={{ fontSize: 12, color: "#666" }}>{e.sub}{!e.counts && " · skoru etkilemez"}</div>
                    </div>
                  ))}
                {!t.detail.cancellations.length && !manual.length && <div style={{ color: "#888" }}>Kayıt yok.</div>}
              </Col>
              <Col xs={24} md={12}>
                <h4>Öğrenci yorumları</h4>
                <div style={{ fontSize: 12, color: "#888", marginBottom: 8 }}>
                  Qarint sisteminden alınan, salt okunur değerlendirmeler. Yetersiz = 1★, Normal = 3★, Harika = 5★.
                </div>
                {t.detail.reviews.map((r) => (
                  <div key={r.id} style={{ borderBottom: "1px solid #f5f5f5", padding: "6px 0" }}>
                    <b>{EVALUATION_LABELS[r.evaluation]}</b> <span style={{ color: "#888", fontSize: 12 }}>· {fmtDay(r.day)}</span>
                    <div style={{ fontSize: 12, color: "#666" }}>{reviewerLabel(r)}</div>
                    {r.description && <div style={{ color: "#555" }}>{r.description}</div>}
                  </div>
                ))}
                {!t.detail.reviews.length && <div style={{ color: "#888" }}>Yorum yok.</div>}
              </Col>
            </Row>
          </>
        )}
      </Spin>
    </Modal>
  );
};

const PerformanceView = ({ teachers, managerNames, days, loading }) => {
  const [detailId, setDetailId] = useState(null);
  const [search, setSearch] = useState("");
  const [emailSearch, setEmailSearch] = useState("");
  const [bandFilter, setBandFilter] = useState("all");
  const [activeFilter, setActiveFilter] = useState("all");

  // Kartlar manager filtresindeki tüm eğitmenleri, tablo ve CSV arama sonucunu gösterir
  const rows = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr-TR");
    const e = emailSearch.trim().toLocaleLowerCase("tr-TR");
    return teachers.filter(
      (t) =>
        (!q || teacherName(t).toLocaleLowerCase("tr-TR").includes(q)) &&
        (!e || (t.email || "").toLocaleLowerCase("tr-TR").includes(e)) &&
        (bandFilter === "all" || (bandFilter === "provisional" ? t.perf.provisional : t.perf.band === bandFilter)) &&
        matchesActive(t, activeFilter)
    );
  }, [teachers, search, emailSearch, bandFilter, activeFilter]);

  const scored = teachers.filter((t) => t.perf.bookings > 0 || t.perf.reviewCount > 0);
  const avg = scored.length ? scored.reduce((s, t) => s + t.perf.score, 0) / scored.length : 0;
  const sum = (k) => teachers.reduce((s, t) => s + t.perf[k], 0);

  const exportCSV = () => {
    downloadCSV(
      `qarint-performans-${days[0]}_${days[days.length - 1]}.csv`,
      ["Eğitmen", "Email", "Manager", "Rezervasyon", "İptal", "Kaçırılan", "Geç kalma", "Kalite ihlali", "Yorum adedi",
        "Ort. yıldız", "İptal /40", "Disiplin /25", "Yorum /35", "Toplam skor", "Durum", "Geçici"],
      rows.map((t) => [
        teacherName(t), t.email, managerNames[t.manager_id] || "Atanmamış", t.perf.bookings, t.perf.cancels, t.perf.misses,
        t.perf.late, t.perf.quality, t.perf.reviewCount, fmtDec(t.perf.avgStars), fmtDec(t.perf.cancellation),
        fmtDec(t.perf.discipline), fmtDec(t.perf.reviews), fmtDec(t.perf.score), t.perf.band, t.perf.provisional ? "Evet" : "Hayır",
      ])
    );
  };

  const columns = [
    {
      title: "Eğitmen",
      key: "name",
      render: (_, t) => (
        <>
          {teacherName(t)} {Number(t.user_status) !== 1 && <Tag>Pasif</Tag>}
        </>
      ),
      sorter: (a, b) => teacherName(a).localeCompare(teacherName(b)),
    },
    { title: "Manager", key: "manager", render: (_, t) => managerNames[t.manager_id] || <span style={{ color: "#bbb" }}>Atanmamış</span> },
    { title: "Rezervasyon", key: "bookings", align: "right", render: (_, t) => t.perf.bookings, sorter: (a, b) => a.perf.bookings - b.perf.bookings },
    { title: "İptal", key: "cancels", align: "right", render: (_, t) => t.perf.cancels },
    { title: "Kaçırılan", key: "misses", align: "right", render: (_, t) => t.perf.misses },
    { title: "Diğer ihlal", key: "other", align: "right", render: (_, t) => t.perf.late + t.perf.quality },
    { title: "Öğrenci yorumu", key: "reviews", align: "right", render: (_, t) => (t.perf.reviewCount ? `${t.perf.reviewCount} · ${fmtDec(t.perf.avgStars)}★` : "-") },
    { title: "İptal /40", key: "c", align: "right", render: (_, t) => fmtDec(t.perf.cancellation) },
    { title: "Disiplin /25", key: "d", align: "right", render: (_, t) => fmtDec(t.perf.discipline) },
    { title: "Yorum /35", key: "r", align: "right", render: (_, t) => fmtDec(t.perf.reviews) },
    {
      title: "Toplam skor", key: "score", align: "right", defaultSortOrder: "descend",
      sorter: (a, b) => a.perf.score - b.perf.score,
      render: (_, t) => <b>{fmtDec(t.perf.score)}</b>,
    },
    {
      title: "Durum", key: "band",
      render: (_, t) => (
        <>
          <Tag color={BAND_COLORS[t.perf.band]}>{t.perf.band}</Tag>
          {t.perf.provisional && <Tag>Geçici</Tag>}
        </>
      ),
    },
    { title: "", key: "act", render: (_, t) => <Button size="small" onClick={() => setDetailId(t.id)}>İncele</Button> },
  ];

  return (
    <>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}><Card><Statistic title="Ortalama performans" value={fmtDec(avg)} suffix="/ 100" /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title="Yönetici iptali (adet)" value={sum("cancels")} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title="Öğretmen kaçırdı (adet)" value={sum("misses")} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title="Öğrenci yorumları" value={sum("reviewCount")} /></Card></Col>
      </Row>
      <Alert
        type="info"
        style={{ marginBottom: 16 }}
        message="Qarint Öğretmen Performans Skoru (100 puan)"
        description="Sistemden gelen iptal oranı 40p + manuel girilen kaçırılan ders / ihlal 25p + sistemden gelen öğrenci puanları 35p. Öğrenci kaynaklı iptaller puanı düşürmez. Az veri varsa (30'dan az rezervasyon veya 10'dan az yorum) skor “Geçici” işaretlenir. Puan tek başına otomatik ücret düşürme veya işten çıkarma kararı üretmez. Silinen dersler sistemde kayıt tutulmadığı için şimdilik skora dahil değildir."
      />
      <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
        <Space wrap>
          <Input placeholder="Ad / Soyad" allowClear value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 200 }} />
          <Input placeholder="Email" allowClear value={emailSearch} onChange={(e) => setEmailSearch(e.target.value)} style={{ width: 200 }} />
          <Select value={bandFilter} onChange={setBandFilter} style={{ width: 180 }} options={[
            { value: "all", label: "Durum (tümü)" },
            ...Object.keys(BAND_COLORS).map((b) => ({ value: b, label: b })),
            { value: "provisional", label: "Geçici (az veri)" },
          ]} />
          <Select value={activeFilter} onChange={setActiveFilter} style={{ width: 180 }} options={ACTIVE_OPTIONS} />
          <span style={{ color: "#888" }}>{rows.length} eğitmen</span>
        </Space>
        <Button onClick={exportCSV} disabled={!rows.length}>⇩ CSV Rapor</Button>
      </div>
      <Table rowKey="id" loading={loading} columns={columns} dataSource={rows} scroll={{ x: true }} size="middle" />
      <PerformanceDetail teacherId={detailId} days={days} onClose={() => setDetailId(null)} />
    </>
  );
};

export default PerformanceView;
